import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * AiOrb — the living holographic avatar of the assistant.
 * A Three.js GPU particle being: a slowly rotating cosmic SPHERE at rest that
 * MORPHS into a luminous "cosmic eye" (core + corona filaments + rays + stars)
 * whenever Sara speaks. Pure WebGL point cloud, additive blending, no face.
 * Faithful port of the user's artifact — no on-canvas buttons.
 * Reacts in real time to:
 *   - micLevelRef.current  (0..1)  — operator voice while listening
 *   - speakLevelRef.current(0..1)  — Sara's voice while speaking (lip/vibration)
 *   - state: "idle" | "listening" | "thinking" | "speaking"
 */
export function AiOrb({ state = "idle", micLevelRef, speakLevelRef, className, size }) {
  const containerRef = useRef(null);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

    let W = Math.max(1, container.clientWidth || 300);
    let H = Math.max(1, container.clientHeight || 300);
    const area = Math.min(W, H);

    // particle budget scaled to rendered size (tiny floating orb → few points)
    let q;
    if (area < 130) q = 0.24;
    else if (isMobile) q = 0.5;
    else q = 1;

    const CORE_COUNT = Math.round(1200 * q);
    const CORONA_COUNT = Math.round(4500 * q);
    const RAY_COUNT = Math.round(6000 * q);
    const STAR_COUNT = Math.round(800 * q);
    const TOTAL_COUNT = CORE_COUNT + CORONA_COUNT + RAY_COUNT + STAR_COUNT;

    const CORE_RADIUS = 0.9;
    const CORONA_INNER = 1.0, CORONA_OUTER = 2.0;
    const RAY_INNER = 1.6, RAY_OUTER = 6.0;

    const positions = new Float32Array(TOTAL_COUNT * 3);
    const colors = new Float32Array(TOTAL_COUNT * 3);
    const spherePositions = new Float32Array(TOTAL_COUNT * 3);
    const eyePositions = new Float32Array(TOTAL_COUNT * 3);
    const particleTypes = new Uint8Array(TOTAL_COUNT);
    const particleAngles = new Float32Array(TOTAL_COUNT);
    const particleRadii = new Float32Array(TOTAL_COUNT);
    const particleSeed = new Float32Array(TOTAL_COUNT);
    const particleZ = new Float32Array(TOTAL_COUNT);

    let idx = 0;

    // ---- CORE (blue nucleus disc) ----
    for (let i = 0; i < CORE_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const r = CORE_RADIUS * Math.sqrt(Math.random());
      eyePositions[idx * 3] = Math.cos(angle) * r;
      eyePositions[idx * 3 + 1] = Math.sin(angle) * r;
      eyePositions[idx * 3 + 2] = (Math.random() - 0.5) * 0.3;
      const t = r / CORE_RADIUS;
      colors[idx * 3] = 0.9 - t * 0.8;
      colors[idx * 3 + 1] = 0.98 - t * 0.28;
      colors[idx * 3 + 2] = 1.0;
      particleTypes[idx] = 0;
      particleAngles[idx] = angle;
      particleRadii[idx] = r;
      particleSeed[idx] = Math.random() * Math.PI * 2;
      particleZ[idx] = eyePositions[idx * 3 + 2];
      idx++;
    }

    // ---- CORONA (pink/red breathing filaments) ----
    const coronaFilaments = Math.max(60, Math.round(400 * q));
    const particlesPerFilament = Math.max(1, Math.floor(CORONA_COUNT / coronaFilaments));
    for (let f = 0; f < coronaFilaments && idx < CORE_COUNT + CORONA_COUNT; f++) {
      const filamentAngle = (f / coronaFilaments) * Math.PI * 2 + Math.random() * 0.05;
      const filamentLength = CORONA_INNER + Math.random() * (CORONA_OUTER - CORONA_INNER);
      for (let p = 0; p < particlesPerFilament && idx < CORE_COUNT + CORONA_COUNT; p++) {
        const t = p / particlesPerFilament;
        const r = CORONA_INNER + (filamentLength - CORONA_INNER) * t;
        const angleVar = Math.sin(t * 5 + filamentAngle * 3) * 0.03;
        const angle = filamentAngle + angleVar;
        const perpDispersion = (Math.random() - 0.5) * 0.08 * (1 + t);
        eyePositions[idx * 3] = Math.cos(angle) * r - Math.sin(angle) * perpDispersion;
        eyePositions[idx * 3 + 1] = Math.sin(angle) * r + Math.cos(angle) * perpDispersion;
        eyePositions[idx * 3 + 2] = (Math.random() - 0.5) * 0.4;
        const hueShift = Math.sin(t * 3 + f * 0.5) * 0.05;
        colors[idx * 3] = 0.1 + t * 0.2 + hueShift;
        colors[idx * 3 + 1] = 0.6 + t * 0.2;
        colors[idx * 3 + 2] = 1.0;
        particleTypes[idx] = 1;
        particleAngles[idx] = angle;
        particleRadii[idx] = r;
        particleSeed[idx] = Math.random() * Math.PI * 2;
        particleZ[idx] = eyePositions[idx * 3 + 2];
        idx++;
      }
    }
    while (idx < CORE_COUNT + CORONA_COUNT) {
      const angle = Math.random() * Math.PI * 2;
      const r = CORONA_INNER + Math.random() * (CORONA_OUTER - CORONA_INNER);
      eyePositions[idx * 3] = Math.cos(angle) * r;
      eyePositions[idx * 3 + 1] = Math.sin(angle) * r;
      eyePositions[idx * 3 + 2] = 0;
      colors[idx * 3] = 0.2; colors[idx * 3 + 1] = 0.65; colors[idx * 3 + 2] = 1.0;
      particleTypes[idx] = 1;
      particleAngles[idx] = angle; particleRadii[idx] = r;
      particleSeed[idx] = Math.random() * Math.PI * 2;
      idx++;
    }

    // ---- RAYS (radiating red→purple spikes) ----
    const rayFilaments = Math.max(80, Math.round(700 * q));
    const particlesPerRay = Math.max(1, Math.floor(RAY_COUNT / rayFilaments));
    for (let f = 0; f < rayFilaments && idx < CORE_COUNT + CORONA_COUNT + RAY_COUNT; f++) {
      const rayAngle = (f / rayFilaments) * Math.PI * 2 + (Math.random() - 0.5) * 0.05;
      const rayLen = RAY_INNER + Math.random() * (RAY_OUTER - RAY_INNER);
      for (let p = 0; p < particlesPerRay && idx < CORE_COUNT + CORONA_COUNT + RAY_COUNT; p++) {
        const t = p / particlesPerRay;
        const r = RAY_INNER + (rayLen - RAY_INNER) * t;
        const angleVar = Math.sin(t * 4 + rayAngle * 2) * 0.02 * (1 - t);
        const angle = rayAngle + angleVar;
        const perpDispersion = (Math.random() - 0.5) * 0.05 * (1 - t * 0.5);
        eyePositions[idx * 3] = Math.cos(angle) * r - Math.sin(angle) * perpDispersion;
        eyePositions[idx * 3 + 1] = Math.sin(angle) * r + Math.cos(angle) * perpDispersion;
        eyePositions[idx * 3 + 2] = (Math.random() - 0.5) * 0.6;
        const rC = 0.15 + t * 0.4, gC = 0.5 - t * 0.35, bC = 1.0;
        colors[idx * 3] = rC; colors[idx * 3 + 1] = Math.max(0.1, gC); colors[idx * 3 + 2] = bC;
        particleTypes[idx] = 2;
        particleAngles[idx] = angle; particleRadii[idx] = r;
        particleSeed[idx] = Math.random() * Math.PI * 2;
        particleZ[idx] = eyePositions[idx * 3 + 2];
        idx++;
      }
    }
    while (idx < CORE_COUNT + CORONA_COUNT + RAY_COUNT) {
      const angle = Math.random() * Math.PI * 2;
      const r = RAY_INNER + Math.random() * (RAY_OUTER - RAY_INNER);
      eyePositions[idx * 3] = Math.cos(angle) * r;
      eyePositions[idx * 3 + 1] = Math.sin(angle) * r;
      eyePositions[idx * 3 + 2] = 0;
      const t = (r - RAY_INNER) / (RAY_OUTER - RAY_INNER);
      colors[idx * 3] = 0.2 + t * 0.35; colors[idx * 3 + 1] = 0.45; colors[idx * 3 + 2] = 1.0;
      particleTypes[idx] = 2;
      particleAngles[idx] = angle; particleRadii[idx] = r;
      particleSeed[idx] = Math.random() * Math.PI * 2;
      idx++;
    }

    // ---- STARS (far ambient white twinkles) ----
    for (let i = 0; i < STAR_COUNT; i++) {
      const angle = Math.random() * Math.PI * 2;
      const r = 3.0 + Math.random() * 6.0;
      eyePositions[idx * 3] = Math.cos(angle) * r;
      eyePositions[idx * 3 + 1] = Math.sin(angle) * r;
      eyePositions[idx * 3 + 2] = (Math.random() - 0.5) * 3.0;
      colors[idx * 3] = 0.85 + Math.random() * 0.15;
      colors[idx * 3 + 1] = 0.9 + Math.random() * 0.1;
      colors[idx * 3 + 2] = 1.0;
      particleTypes[idx] = 3;
      particleAngles[idx] = angle; particleRadii[idx] = r;
      particleSeed[idx] = Math.random() * Math.PI * 2;
      particleZ[idx] = eyePositions[idx * 3 + 2];
      idx++;
    }

    // ---- SPHERE (rest state: evenly scattered shell) ----
    const SPHERE_RADIUS = 4.0;
    for (let i = 0; i < TOTAL_COUNT; i++) {
      const u = Math.random(), v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = SPHERE_RADIUS * (0.96 + Math.random() * 0.08);
      spherePositions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      spherePositions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      spherePositions[i * 3 + 2] = r * Math.cos(phi);
    }

    // ---- THREE scene ----
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 1000);
    camera.position.set(0, 0, 16);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: !isMobile, alpha: true, powerPreference: "high-performance" });
    renderer.setSize(W, H, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isMobile ? 1.75 : 2));
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    container.appendChild(renderer.domElement);

    const geometry = new THREE.BufferGeometry();
    positions.set(spherePositions);
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    const material = new THREE.PointsMaterial({
      size: 0.07,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
      opacity: 0.95,
    });
    const particles = new THREE.Points(geometry, material);
    scene.add(particles);
    const positionAttribute = geometry.attributes.position;
    const currentPositions = positionAttribute.array;

    const ro = new ResizeObserver(() => {
      W = Math.max(1, container.clientWidth || W);
      H = Math.max(1, container.clientHeight || H);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
      renderer.setSize(W, H, false);
    });
    ro.observe(container);

    // ---- animation ----
    const clock = new THREE.Clock();
    let morphState = 0;
    let thinkingOscillation = 0;
    let mouthOpen = 0;
    let listenEnergy = 0;
    let raf = 0;

    const animate = () => {
      const delta = Math.min(clock.getDelta(), 0.05);
      const elapsedTime = performance.now() * 0.001;
      const st = stateRef.current;
      const mic = (micLevelRef && micLevelRef.current) || 0;
      const spk = (speakLevelRef && speakLevelRef.current) || 0;

      // energy = loudest of Sara's own voice (TTS) OR the operator's mic — the Core
      // reacts to BOTH speakers.
      const energy = Math.max(mic, spk);
      // sphere (plasma) ⇄ neural iris morph — the iris forms whenever a voice is active
      const voiceActive = st === "speaking" || st === "listening";
      const morphTarget = voiceActive ? 1 : 0;
      const morphSpeed = voiceActive ? 0.06 : 0.05;
      const md = morphTarget - morphState;
      morphState += md * morphSpeed;
      if (Math.abs(md) < 0.002) morphState = morphTarget;

      // thinking turbulence
      if (st === "thinking") thinkingOscillation = Math.min(1, thinkingOscillation + delta * 2);
      else thinkingOscillation = Math.max(0, thinkingOscillation - delta * 2);

      // listening breath driven by the operator's real mic level
      const listenTarget = st === "listening" ? mic : 0;
      listenEnergy += (listenTarget - listenEnergy) * 0.15;

      // iris pulse / cybernetic pupil driven by the real audio of whoever is talking
      mouthOpen *= Math.pow(0.9, delta * 60);
      if (voiceActive) {
        const base = energy > 0.02
          ? 0.12 + Math.min(1, energy * 1.3)
          : 0.14 + Math.sin(elapsedTime * 16) * 0.08 + Math.sin(elapsedTime * 27) * 0.04;
        mouthOpen = Math.max(mouthOpen, base);
      } else {
        mouthOpen *= 0.9;
      }
      mouthOpen = Math.min(1, mouthOpen);
      const audioIntensity = mouthOpen * (0.6 + 0.4 * Math.sin(elapsedTime * 22));

      const time = elapsedTime * 0.8;
      const pulse = 1 + Math.sin(elapsedTime * 1.5) * 0.04 + listenEnergy * 0.12;

      for (let i = 0; i < TOTAL_COUNT; i++) {
        const i3 = i * 3;
        const sx = spherePositions[i3], sy = spherePositions[i3 + 1], sz = spherePositions[i3 + 2];
        const type = particleTypes[i];
        const angle = particleAngles[i];
        const baseRadius = particleRadii[i];
        const seed = particleSeed[i];

        let targetR = baseRadius;
        let targetAngle = angle;
        if (type === 0) {
          targetR = baseRadius * (1 + Math.sin(elapsedTime * 2 + seed) * 0.02);
        } else if (type === 1) {
          targetR = baseRadius * (1 + Math.sin(elapsedTime * 1.5 + seed) * 0.08);
        } else if (type === 2) {
          targetR = baseRadius * (1 + Math.sin(elapsedTime * 1.2 + seed * 0.5) * 0.15);
          targetAngle = angle + Math.sin(elapsedTime * 0.5 + seed) * 0.02;
        } else if (type === 3) {
          targetR = baseRadius + Math.sin(elapsedTime * 3 + seed) * 0.05;
          targetAngle = angle + elapsedTime * 0.02;
        }

        const ex = Math.cos(targetAngle) * targetR * pulse;
        const ey = Math.sin(targetAngle) * targetR * pulse;
        const ez = particleZ[i] * pulse;

        let px = sx * (1 - morphState) + ex * morphState;
        let py = sy * (1 - morphState) + ey * morphState;
        let pz = sz * (1 - morphState) + ez * morphState;

        if (thinkingOscillation > 0.01) {
          const osc = thinkingOscillation;
          px += Math.sin(elapsedTime * 20 + i * 0.01) * 0.15 * osc;
          py += Math.cos(elapsedTime * 22 + i * 0.013) * 0.15 * osc;
          pz += Math.sin(elapsedTime * 18 + i * 0.017) * 0.1 * osc;
        }

        const nx = Math.sin(px * 0.9 + time) * Math.cos(py * 0.8 + time * 0.7) * Math.sin(pz * 0.7 + time * 0.5);
        const ny = Math.cos(px * 0.7 + time * 0.6) * Math.sin(py * 0.9 + time * 0.8) * Math.cos(pz * 0.8 + time * 0.4);
        const nz = Math.sin(px * 0.8 + time * 0.9) * Math.cos(py * 0.7 + time * 0.5) * Math.sin(pz * 0.9 + time * 0.7);
        const noiseAmp = ((1 - morphState) * 0.35 + morphState * 0.02) * (1 + listenEnergy * 0.9);
        px += nx * noiseAmp;
        py += ny * noiseAmp;
        pz += nz * noiseAmp;

        if (morphState > 0.01 && audioIntensity > 0) {
          const vibX = Math.sin(i * 13.7 + elapsedTime * 40) * Math.cos(i * 7.3 + elapsedTime * 25);
          const vibY = Math.cos(i * 11.1 + elapsedTime * 35) * Math.sin(i * 5.9 + elapsedTime * 30);
          const radialVib = 1 + (vibX + vibY) * 0.5 * audioIntensity * 0.05;
          px *= radialVib;
          py *= radialVib;
        }

        currentPositions[i3] = px;
        currentPositions[i3 + 1] = py;
        currentPositions[i3 + 2] = pz;
      }
      positionAttribute.needsUpdate = true;

      let rotSpeed = 0.1 * (1 - morphState) + 0.15 * morphState;
      rotSpeed += thinkingOscillation * 0.3;
      particles.rotation.z += rotSpeed * delta;

      material.size = (0.055 + morphState * 0.025) * (1 + listenEnergy * 0.25);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };

    const onVis = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) raf = requestAnimationFrame(animate);
    };
    document.addEventListener("visibilitychange", onVis);
    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      try { renderer.forceContextLoss(); } catch (_) {}
      if (renderer.domElement && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, [micLevelRef, speakLevelRef]);

  return (
    <div
      ref={containerRef}
      data-testid="ai-orb"
      className={className || ""}
      style={size ? { width: size, height: size } : undefined}
    />
  );
}

export default AiOrb;
